import { TrendingUp, TrendingDown, Minus, Lock } from "lucide-react";
import { cn } from "../../../lib/utils";
import { useLocale } from "../../../context/LanguageContext";

interface MetricCardProps {
  label: string;
  value: string;
  subtitle?: string;
  trend?: number;
  icon?: React.ReactNode;
  loading?: boolean;
  /**
   * When true, render a Lock + "—" placeholder instead of the value/trend.
   * Caller passes the parent PeriodStats._suppressed flag here so every card
   * on the dashboard is suppressed atomically when the cohort is too small.
   */
  suppressed?: boolean;
}

export function MetricCard({
  label,
  value,
  subtitle,
  trend,
  icon,
  loading = false,
  suppressed = false,
}: MetricCardProps) {
  const { t } = useLocale();

  if (loading) {
    return <MetricCardSkeleton />;
  }

  if (suppressed) {
    return (
      <div
        className="rounded-lg border border-border bg-card p-6"
        title={t("dashboard.suppressed.tooltip")}
      >
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <Lock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </div>
        <p
          className="mt-2 text-2xl font-bold text-muted-foreground"
          aria-label={t("dashboard.suppressed.tooltip")}
        >
          —
        </p>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t("dashboard.suppressed.cardSubtitle")}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        {icon && (
          <div className="text-muted-foreground">{icon}</div>
        )}
      </div>
      <p className="mt-2 text-2xl font-bold text-foreground">{value}</p>
      <div className="mt-1 flex items-center gap-2">
        {trend !== undefined && <TrendBadge value={trend} />}
        {subtitle && (
          <span className="text-xs text-muted-foreground">{subtitle}</span>
        )}
      </div>
    </div>
  );
}

function TrendBadge({ value }: { value: number }) {
  const isPositive = value > 0;
  const isNeutral = value === 0;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium",
        isPositive && "text-emerald-600 dark:text-emerald-400",
        !isPositive && !isNeutral && "text-red-500 dark:text-red-400",
        isNeutral && "text-muted-foreground",
      )}
    >
      {isPositive ? (
        <TrendingUp className="h-3 w-3" />
      ) : isNeutral ? (
        <Minus className="h-3 w-3" />
      ) : (
        <TrendingDown className="h-3 w-3" />
      )}
      {isPositive ? "+" : ""}
      {value}%
    </span>
  );
}

function MetricCardSkeleton() {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="h-4 w-24 animate-pulse rounded bg-muted" />
      <div className="mt-3 h-7 w-20 animate-pulse rounded bg-muted" />
      <div className="mt-2 h-3 w-32 animate-pulse rounded bg-muted" />
    </div>
  );
}
